import React, { useEffect, useState } from "react"

import { useQuery } from "@tanstack/react-query"
import { format } from "date-fns"
import { AlertCircle, Calendar, Check, Edit, ExternalLink } from "lucide-react"

import { Alert, AlertDescription } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { Calendar as CalendarComponent } from "@/components/ui/calendar"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { useAuth } from "@/contexts/AuthContext"
import { useUpdatePerson } from "@/hooks/useUpdatePerson"
import { supabase } from "@/lib/supabase"
import { Person } from "@/types/person"

import type { TablesUpdate } from "../../../../database.types"

interface PersonalDetailsTabProps {
  person: Person
}

const PersonalDetailsTab: React.FC<PersonalDetailsTabProps> = ({ person }) => {
  const [personData, setPersonData] = useState(person)
  const [openDialog, setOpenDialog] = useState<string | null>(null)
  const [formState, setFormState] = useState({ ...person })
  const { updatePerson, isUpdating, updateError } = useUpdatePerson()
  const { claims } = useAuth()

  const { data: applications, isLoading: applicationsLoading } = useQuery({
    queryKey: ["person-applications", person.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("applications")
        .select("id, application_id, created_at")
        .eq("person_id", person.id)
        .order("created_at", { ascending: false })

      if (error) throw error
      return data || []
    },
    enabled: !!person.id,
  })

  const teamPermissions = claims.team_permissions || []
  const canEditPersonal = teamPermissions.includes("update.tab.personal")

  useEffect(() => {
    setPersonData(person)
  }, [person])

  const handleOpenDialog = (dialogName: string) => {
    setFormState({ ...personData })
    setOpenDialog(dialogName)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!canEditPersonal) return

    try {
      const fields: TablesUpdate<"people"> = {}
      if (openDialog === "basic") {
        if (formState.first_name !== personData.first_name) {
          fields.first_name = formState.first_name
        }

        if (formState.surname !== personData.surname) {
          fields.surname = formState.surname
        }

        if (formState.phone_number !== personData.phone_number) {
          fields.phone_number = formState.phone_number
        }

        if (formState.email !== personData.email) {
          fields.email = formState.email
        }

        if (formState.sex !== personData.sex) {
          fields.sex = formState.sex
        }

        const nextDob = formState.date_of_birth || null
        const currentDob = personData.date_of_birth || null
        if (nextDob !== currentDob) {
          fields.date_of_birth = nextDob
        }

        const nextVehicleCompany = formState.vehicle_rental_company
        if (nextVehicleCompany !== personData.vehicle_rental_company) {
          fields.vehicle_rental_company = nextVehicleCompany
        }
      } else if (openDialog === "address") {
        if (formState.address_line_1 !== personData.address_line_1) {
          fields.address_line_1 = formState.address_line_1
        }

        if (formState.address_line_2 !== personData.address_line_2) {
          fields.address_line_2 = formState.address_line_2
        }

        if (formState.city !== personData.city) {
          fields.city = formState.city
        }

        if (formState.postcode !== personData.postcode) {
          fields.postcode = formState.postcode
        }
      } else if (openDialog === "nextOfKin") {
        if (formState.next_of_kin_name !== personData.next_of_kin_name) {
          fields.next_of_kin_name = formState.next_of_kin_name
        }

        if (formState.next_of_kin_phone !== personData.next_of_kin_phone) {
          fields.next_of_kin_phone = formState.next_of_kin_phone
        }

        if (formState.next_of_kin_relation !== personData.next_of_kin_relation) {
          fields.next_of_kin_relation = formState.next_of_kin_relation
        }
      }

      if (Object.keys(fields).length > 0) {
        const response = await updatePerson({
          id: personData.id,
          fields,
        })
        const updatedPerson = { ...personData, ...response }
        setFormState(updatedPerson)
        setPersonData(updatedPerson)
        setOpenDialog(null)
      }
    } catch (error) {
      console.error("Error updating details:", error)
    }
  }

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "Not provided"
    const parsed = new Date(dateString)
    if (Number.isNaN(parsed.getTime())) {
      return dateString
    }
    return format(parsed, "PPP")
  }

  const selectedDob = formState.date_of_birth
    ? /^(\d{4})-(\d{2})-(\d{2})$/.test(formState.date_of_birth)
      ? new Date(
          Number(formState.date_of_birth.slice(0, 4)),
          Number(formState.date_of_birth.slice(5, 7)) - 1,
          Number(formState.date_of_birth.slice(8, 10)),
        )
      : new Date(formState.date_of_birth)
    : undefined

  const handleDobSelect = (date?: Date) => {
    setFormState((prev) => ({
      ...prev,
      date_of_birth: date ? format(date, "yyyy-MM-dd") : null,
    }))
  }

  return (
    <div className="space-y-6">
      {updateError && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            {updateError instanceof Error ? updateError.message : "Failed to update"}
          </AlertDescription>
        </Alert>
      )}

      {/* Basic Information */}
      <Dialog
        open={openDialog === "basic"}
        onOpenChange={(v) => (v ? handleOpenDialog("basic") : setOpenDialog(null))}
      >
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Basic Information</CardTitle>
            {canEditPersonal && (
              <DialogTrigger asChild>
                <Button variant="outline" size="sm">
                  <Edit className="h-4 w-4 mr-2" />
                  Edit
                </Button>
              </DialogTrigger>
            )}
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-palette-jungleGreen700">Person ID</label>
                <p className="mt-1 text-sm text-palette-jungleGreen900">
                  {personData.person_id || "Not provided"}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-palette-jungleGreen700">
                  First Name
                </label>
                <p className="mt-1 text-sm text-palette-jungleGreen900">
                  {personData.first_name || "Not provided"}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-palette-jungleGreen700">Surname</label>
                <p className="mt-1 text-sm text-palette-jungleGreen900">
                  {personData.surname || "Not provided"}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-palette-jungleGreen700">
                  Phone Number
                </label>
                <p className="mt-1 text-sm text-palette-jungleGreen900">
                  {personData.phone_number || "Not provided"}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-palette-jungleGreen700">
                  Email Address
                </label>
                <p className="mt-1 text-sm text-palette-jungleGreen900">
                  {personData.email || "Not provided"}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-palette-jungleGreen700">
                  Date of Birth
                </label>
                <p className="mt-1 text-sm text-palette-jungleGreen900">
                  {formatDate(personData.date_of_birth)}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-palette-jungleGreen700">Sex</label>
                <p className="mt-1 text-sm text-palette-jungleGreen900">
                  {personData.sex || "Not provided"}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-palette-jungleGreen700">
                  Vehicle Rental Company
                </label>
                <p className="mt-1 text-sm text-palette-jungleGreen900">
                  {personData.vehicle_rental_company || "Not provided"}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Basic Information</DialogTitle>
          </DialogHeader>
          <form className="space-y-4" onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-palette-jungleGreen700">
                  First Name
                </label>
                <Input
                  value={formState.first_name || ""}
                  onChange={(e) =>
                    setFormState({
                      ...formState,
                      first_name: e.target.value,
                    })
                  }
                  placeholder="Enter first name"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-palette-jungleGreen700">Surname</label>
                <Input
                  value={formState.surname || ""}
                  onChange={(e) =>
                    setFormState({
                      ...formState,
                      surname: e.target.value,
                    })
                  }
                  placeholder="Enter surname"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-palette-jungleGreen700">
                  Phone Number
                </label>
                <Input
                  value={formState.phone_number || ""}
                  onChange={(e) =>
                    setFormState({
                      ...formState,
                      phone_number: e.target.value,
                    })
                  }
                  placeholder="Enter phone number"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-palette-jungleGreen700">
                  Email Address
                </label>
                <Input
                  type="email"
                  value={formState.email || ""}
                  onChange={(e) =>
                    setFormState({
                      ...formState,
                      email: e.target.value,
                    })
                  }
                  placeholder="Enter email address"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-palette-jungleGreen700">
                  Date of Birth
                </label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-start text-left font-normal"
                    >
                      <Calendar className="mr-2 h-4 w-4" />
                      {selectedDob ? format(selectedDob, "PPP") : "Pick a date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <CalendarComponent
                      mode="single"
                      selected={selectedDob}
                      onSelect={handleDobSelect}
                      weekStartsOn={1}
                      captionLayout="dropdown"
                      fromYear={1900}
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div>
                <label className="text-sm font-medium text-palette-jungleGreen700">Sex</label>
                <Select
                  value={formState.sex || ""}
                  onValueChange={(value) => setFormState({ ...formState, sex: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select sex" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Male">Male</SelectItem>
                    <SelectItem value="Female">Female</SelectItem>
                    <SelectItem value="Non-Binary">Non-Binary</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                    <SelectItem value="Prefer Not Say">Prefer not to say</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-palette-jungleGreen700">
                  Vehicle Rental Company
                </label>
                <Select
                  value={formState.vehicle_rental_company || ""}
                  onValueChange={(value) =>
                    setFormState({
                      ...formState,
                      vehicle_rental_company: value,
                    })
                  }
                >
                  <SelectTrigger className="text-left">
                    <SelectValue placeholder="Select vehicle rental company" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="HIVED Vehicle">HIVED Vehicle</SelectItem>
                    <SelectItem value="Slipstream Vehicle">Slipstream Vehicle</SelectItem>
                    <SelectItem value="Own Vehicle">Own Vehicle</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex justify-end space-x-2">
              <Button type="button" variant="outline" onClick={() => setOpenDialog(null)}>
                Cancel
              </Button>
              <Button type="submit" disabled={isUpdating}>
                {isUpdating ? (
                  <>Saving...</>
                ) : (
                  <>
                    <Check className="h-4 w-4 mr-2" />
                    Save Changes
                  </>
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Address Information */}
      <Dialog
        open={openDialog === "address"}
        onOpenChange={(v) => (v ? handleOpenDialog("address") : setOpenDialog(null))}
      >
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Address Information</CardTitle>
            {canEditPersonal && (
              <DialogTrigger asChild>
                <Button variant="outline" size="sm">
                  <Edit className="h-4 w-4 mr-2" />
                  Edit
                </Button>
              </DialogTrigger>
            )}
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-palette-jungleGreen700">
                  Address Line 1
                </label>
                <p className="mt-1 text-sm text-palette-jungleGreen900">
                  {personData.address_line_1 || "Not provided"}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-palette-jungleGreen700">
                  Address Line 2
                </label>
                <p className="mt-1 text-sm text-palette-jungleGreen900">
                  {personData.address_line_2 || "Not provided"}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-palette-jungleGreen700">City</label>
                <p className="mt-1 text-sm text-palette-jungleGreen900">
                  {personData.city || "Not provided"}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-palette-jungleGreen700">Postcode</label>
                <p className="mt-1 text-sm text-palette-jungleGreen900">
                  {personData.postcode || "Not provided"}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Address Information</DialogTitle>
          </DialogHeader>
          <form className="space-y-4" onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 gap-4">
              <div>
                <label className="text-sm font-medium text-palette-jungleGreen700">
                  Address Line 1
                </label>
                <Input
                  value={formState.address_line_1 || ""}
                  onChange={(e) =>
                    setFormState({
                      ...formState,
                      address_line_1: e.target.value,
                    })
                  }
                  placeholder="Enter address line 1"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-palette-jungleGreen700">
                  Address Line 2
                </label>
                <Input
                  value={formState.address_line_2 || ""}
                  onChange={(e) =>
                    setFormState({
                      ...formState,
                      address_line_2: e.target.value,
                    })
                  }
                  placeholder="Enter address line 2"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-palette-jungleGreen700">City</label>
                <Input
                  value={formState.city || ""}
                  onChange={(e) =>
                    setFormState({
                      ...formState,
                      city: e.target.value,
                    })
                  }
                  placeholder="Enter city"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-palette-jungleGreen700">Postcode</label>
                <Input
                  value={formState.postcode || ""}
                  onChange={(e) =>
                    setFormState({
                      ...formState,
                      postcode: e.target.value,
                    })
                  }
                  placeholder="Enter postcode"
                />
              </div>
            </div>
            <div className="flex justify-end space-x-2">
              <Button type="button" variant="outline" onClick={() => setOpenDialog(null)}>
                Cancel
              </Button>
              <Button type="submit" disabled={isUpdating}>
                {isUpdating ? (
                  <>Saving...</>
                ) : (
                  <>
                    <Check className="h-4 w-4 mr-2" />
                    Save Changes
                  </>
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Next of Kin Information */}
      <Dialog
        open={openDialog === "nextOfKin"}
        onOpenChange={(v) => (v ? handleOpenDialog("nextOfKin") : setOpenDialog(null))}
      >
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Next of Kin</CardTitle>
            {canEditPersonal && (
              <DialogTrigger asChild>
                <Button variant="outline" size="sm">
                  <Edit className="h-4 w-4 mr-2" />
                  Edit
                </Button>
              </DialogTrigger>
            )}
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-palette-jungleGreen700">Name</label>
                <p className="mt-1 text-sm text-palette-jungleGreen900">
                  {personData.next_of_kin_name || "Not provided"}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-palette-jungleGreen700">Phone</label>
                <p className="mt-1 text-sm text-palette-jungleGreen900">
                  {personData.next_of_kin_phone || "Not provided"}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-palette-jungleGreen700">
                  Relationship
                </label>
                <p className="mt-1 text-sm text-palette-jungleGreen900">
                  {personData.next_of_kin_relation || "Not provided"}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Next of Kin</DialogTitle>
          </DialogHeader>
          <form className="space-y-4" onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 gap-4">
              <div>
                <label className="text-sm font-medium text-palette-jungleGreen700">Name</label>
                <Input
                  value={formState.next_of_kin_name || ""}
                  onChange={(e) =>
                    setFormState({
                      ...formState,
                      next_of_kin_name: e.target.value,
                    })
                  }
                  placeholder="Enter next of kin name"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-palette-jungleGreen700">Phone</label>
                <Input
                  value={formState.next_of_kin_phone || ""}
                  onChange={(e) =>
                    setFormState({
                      ...formState,
                      next_of_kin_phone: e.target.value,
                    })
                  }
                  placeholder="Enter next of kin phone"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-palette-jungleGreen700">
                  Relationship
                </label>
                <Input
                  value={formState.next_of_kin_relation || ""}
                  onChange={(e) =>
                    setFormState({
                      ...formState,
                      next_of_kin_relation: e.target.value,
                    })
                  }
                  placeholder="Enter relationship"
                />
              </div>
            </div>
            <div className="flex justify-end space-x-2">
              <Button type="button" variant="outline" onClick={() => setOpenDialog(null)}>
                Cancel
              </Button>
              <Button type="submit" disabled={isUpdating}>
                {isUpdating ? (
                  <>Saving...</>
                ) : (
                  <>
                    <Check className="h-4 w-4 mr-2" />
                    Save Changes
                  </>
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Application Details */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Application Details</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {applicationsLoading ? (
            <div className="rounded-lg border border-dashed border-palette-jungleGreen300 bg-palette-jungleGreen000 p-4">
              <p className="text-sm text-palette-jungleGreen600">Loading applications…</p>
            </div>
          ) : applications && applications.length > 0 ? (
            <div className="space-y-2">
              {applications.map((app) => (
                <div
                  key={app.id}
                  className="flex items-center justify-between rounded-lg border border-palette-jungleGreen200 bg-white p-4 hover:bg-palette-jungleGreen000 transition-colors"
                >
                  <div className="flex-1 space-y-1">
                    <div className="flex items-center gap-3">
                      <p className="text-sm font-medium text-palette-jungleGreen900">
                        {app.application_id}
                      </p>
                      <p className="text-xs text-palette-jungleGreen500">
                        {app.created_at
                          ? format(new Date(app.created_at), "PPp")
                          : "Date not available"}
                      </p>
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => window.open(`/applications/${app.id}`, "_blank")}
                  >
                    <ExternalLink className="h-4 w-4 mr-2" />
                    View Details
                  </Button>
                </div>
              ))}
            </div>
          ) : (
            <div className="rounded-lg border border-dashed border-palette-jungleGreen300 bg-palette-jungleGreen000 p-4">
              <p className="text-sm text-palette-jungleGreen600">
                No applications found for this person
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

export default PersonalDetailsTab
